import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  InputAdornment,
  Badge,
  Stack,
  TablePagination
} from '@mui/material';

import {
  Search as SearchIcon,
  Person as PersonIcon,
  Science as ScienceIcon,
  Scanner as ScannerIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Schedule as ScheduleIcon,
  LocalHospital as HospitalIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Thermostat as TempIcon,
  Favorite as PulseIcon,
  Air as BreathIcon,
  MonitorWeight as WeightIcon,
  Height as HeightIcon,
  Calculate as BMIIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  ChildCare as ChildCareIcon,
  Clear as ClearIcon,
  PlaylistAddCheck as ActionsIcon
} from '@mui/icons-material';

import { loadScanPatients, loadScanRequestsOutpatient, loadScanRequestsInpatient, loadScanParameters as loadScanParametersAPI, saveScanResults, loadExistingScanResults, loadPatientScanReport, loadPatientScanResultsToday, loadPharmacyInfo, loadPatientDocumentation } from '../BaseURL';
import { generateScanReportHTML } from './ScanReportTemplate';
import Swal from 'sweetalert2';
import MedicalHistoryDialog from '../Doctors/MedicalHistoryDialog';

const RequestedScan = () => {
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [patientScans, setPatientScans] = useState([]);

  /* Facility policy on unpaid orders, from the request-list response.
     Strictest default so an older server never leaves an unpaid scan looking
     workable. */
  const [unpaidPolicy, setUnpaidPolicy] = useState({
    unpaidVisibility: 'hide',
    canServeUnpaid: false,
  });

  // From the server's normalised boolean, not from status text.
  const isUnpaidScan  = (scan) => scan?.isPaid === false;
  const isBlockedScan = (scan) => isUnpaidScan(scan) && !unpaidPolicy.canServeUnpaid;
  const [filterType, setFilterType] = useState('all'); // 'all' | 'InPatient' | 'OutPatient'
  const [filterLoading, setFilterLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Worklist (patient table) state — mirrors MyPatients.js
  const [quickSearchId, setQuickSearchId] = useState('');
  const [patientPage, setPatientPage] = useState(0);
  const [patientRowsPerPage, setPatientRowsPerPage] = useState(10);
  const [patientOrderBy, setPatientOrderBy] = useState('fullName');
  const [patientOrder, setPatientOrder] = useState('asc');

  // Results Entry Dialog States
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);
  const [selectedTestForResults, setSelectedTestForResults] = useState(null);
  const [scanParameters, setScanParameters] = useState([]);
  const [selectedParameter, setSelectedParameter] = useState(null);
  const [parameterResult, setParameterResult] = useState('');
  const [parameterFlag, setParameterFlag] = useState('normal');
  const [enteredResults, setEnteredResults] = useState([]);
  const [loadingParameters, setLoadingParameters] = useState(false);
  const [loadingParameterDetails, setLoadingParameterDetails] = useState(false);
  const [scanCategory, setScanCategory] = useState('');
  const [availableFlags, setAvailableFlags] = useState(['-']);
  const [editingIndex, setEditingIndex] = useState(null);
  const [generalComment, setGeneralComment] = useState('');

  // Report Dialog States
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportData, setReportData] = useState({});
  const [reportLoading, setReportLoading] = useState(false);

  // Documentation Dialog States
  const [documentationDialogOpen, setDocumentationDialogOpen] = useState(false);
  const [documentationData, setDocumentationData] = useState(null);
  const [documentationLoading, setDocumentationLoading] = useState(false);

  // Medical History Dialog State
  const [medicalHistoryOpen, setMedicalHistoryOpen] = useState(false);

  const searchInputRef = useRef(null);

  // Get user info from localStorage
  const userInfo = JSON.parse(localStorage.getItem('userInfo')) || {};

  // Company info state
  const [companyInfo, setCompanyInfo] = useState(null);

  // Load company info
  useEffect(() => {
    const loadCompanyInfo = async () => {
      try {
        const response = await fetch(loadPharmacyInfo);
        const data = await response.json();
        if (data.success && data.results) {
          setCompanyInfo(data.results);
        }
      } catch (error) {
        console.error('Error loading company info:', error);
      }
    };
    loadCompanyInfo();
  }, []);

  // Load all patients once on mount — filter chips apply client-side
  useEffect(() => {
    loadPatients('all');
  }, []);

  const loadPatients = async (state = 'all') => {
    setFilterLoading(true);
    try {
      const response = await fetch(loadScanPatients(state));
      const data = await response.json();
      
      if (Array.isArray(data)) {
        setSearchResults(data);
      } else {
        console.error('Unexpected response format:', data);
        setSearchResults([]);
      }
      setFilterLoading(false);
    } catch (error) {
      console.error('Error loading patients:', error);
      setSearchResults([]);
      setFilterLoading(false);
    }
  };

  // Load patient scan requests when patient is selected
  useEffect(() => {
    if (selectedPatient) {
      loadPatientScanRequests(selectedPatient.id, selectedPatient.state);
    }
  }, [selectedPatient]);

  // Check for stored scan data from dashboard edit on component mount
  useEffect(() => {
    const storedScanData = localStorage.getItem('scanEditData');
    if (storedScanData) {
      try {
        const scanData = JSON.parse(storedScanData);
        console.log('Loading scan data from dashboard edit:', scanData);
        
        // Set the selected patient and scan data
        setSelectedPatient({
          id: scanData.patient_id,
          name: scanData.patient_name,
          state: scanData.patient_state || 'outpatient'
        });
        
        // Load the patient's scan requests first, then open results entry for the specific scan
        loadPatientScanRequests(scanData.patient_id, scanData.patient_state || 'outpatient').then(() => {
          // Find the specific scan in the loaded requests and open results entry
          setTimeout(() => {
            const scanToEdit = {
              ...scanData,
              patient_id: scanData.patient_id
            };
            handleEnterResults(scanToEdit);
          }, 500); // Small delay to ensure scans are loaded
        });
        
        // Clear the stored data
        localStorage.removeItem('scanEditData');
      } catch (error) {
        console.error('Error parsing stored scan data:', error);
        localStorage.removeItem('scanEditData');
      }
    }
  }, []);

  const loadPatientScanRequests = async (patientId, patientState) => {
    try {
      // Load scan requests for the patient
      const endpoint = patientState === 'inpatient' 
        ? loadScanRequestsInpatient
        : loadScanRequestsOutpatient;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ patientId })
      });
      
      const data = await response.json();
      
      // Policy arrives on the SAME response as the rows. Absent => older
      // server: assume the strictest position so no unpaid scan looks workable.
      setUnpaidPolicy({
        unpaidVisibility: data?.unpaidVisibility || 'hide',
        canServeUnpaid:   data?.canServeUnpaid === true,
      });

      if (data.scanRequests && Array.isArray(data.scanRequests)) {
        // Add patient_id to each scan request
        const scansWithPatientId = data.scanRequests.map(scan => ({
          ...scan,
          patient_id: patientId
        }));
        setPatientScans(scansWithPatientId);
      } else {
        setPatientScans([]);
      }
    } catch (error) {
      console.error('Error loading patient scan requests:', error);
      setPatientScans([]);
    }
  }

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setSearchTerm('');
  };

  const handleBackToWorklist = () => {
    setSelectedPatient(null);
    setPatientScans([]);
    setPage(0);
  };

  // Match on the digits only so staff can type "125" and find "OPD-125", "LAB-125", etc.
  const handleQuickSearchById = () => {
    const idQuery = quickSearchId.trim();
    if (!idQuery) return;

    const digitsOf = (val) => String(val ?? '').replace(/\D/g, '');
    const queryDigits = digitsOf(idQuery);

    const match = searchResults.find((p) => {
      if (String(p.id).toLowerCase() === idQuery.toLowerCase()) return true;
      const idDigits = digitsOf(p.id);
      return queryDigits && idDigits && idDigits === queryDigits;
    });

    if (match) {
      setSelectedPatient(match);
      setQuickSearchId('');
    } else {
      Swal.fire({
        icon: 'info',
        title: 'No patient found',
        text: `No patient with ID "${idQuery}" was found on the active scan list.`,
        confirmButtonColor: '#1A9B7F',
      });
    }
  };

  const handleViewDocumentation = async () => {
    if (!selectedPatient) {
      Swal.fire({
        icon: 'warning',
        title: 'No Patient Selected',
        text: 'Please select a patient first.',
        confirmButtonColor: '#1976d2'
      });
      return;
    }

    setDocumentationLoading(true);
    try {
      const url = `${loadPatientDocumentation}?patientId=${encodeURIComponent(selectedPatient.id)}`;
      const response = await fetch(url);
      const data = await response.json();

      if (data.success && data.data) {
        setDocumentationData(data.data);
        setDocumentationDialogOpen(true);
      } else {
        Swal.fire({
          icon: 'info',
          title: 'No Documentation',
          text: 'No documentation records found for this patient.',
          confirmButtonColor: '#1976d2'
        });
      }
    } catch (error) {
      console.error('Error loading documentation:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load patient documentation.',
        confirmButtonColor: '#d32f2f'
      });
    } finally {
      setDocumentationLoading(false);
    }
  };

  const handleCloseDocumentation = () => {
    setDocumentationDialogOpen(false);
    setDocumentationData(null);
  };

  // Normalise the state field returned by the backend into a stable canonical form.
  const canonicalState = (raw) => {
    const s = String(raw || '').toLowerCase();
    if (s === 'inpatient') return 'InPatient';
    if (s === 'outpatient') return 'OutPatient';
    return raw || '—';
  };

  const initialsOf = (name = '') =>
    name.split(/\s+/).filter(Boolean).slice(0, 2).map(n => n[0]).join('').toUpperCase() || '?';

  // Counts for the filter chips
  const patientCounts = useMemo(() => {
    const c = { all: searchResults.length, InPatient: 0, OutPatient: 0 };
    searchResults.forEach(p => {
      const s = canonicalState(p.state);
      if (s === 'InPatient') c.InPatient++;
      else if (s === 'OutPatient') c.OutPatient++;
    });
    return c;
  }, [searchResults]);

  // Apply search + status filter, then sort
  const visiblePatients = useMemo(() => {
    const q = (searchTerm || '').trim().toLowerCase();
    const filtered = searchResults.filter(p => {
      const s = canonicalState(p.state);
      if (filterType === 'InPatient' && s !== 'InPatient') return false;
      if (filterType === 'OutPatient' && s !== 'OutPatient') return false;
      if (!q) return true;
      return (
        (p.fullName || '').toLowerCase().includes(q)
        || String(p.id || '').toLowerCase().includes(q)
        || String(p.tel || '').includes(q)
        || (p.Insurance || '').toLowerCase().includes(q)
      );
    });
    const cmp = (a, b) => {
      const av = patientOrderBy === 'state' ? canonicalState(a.state) : a[patientOrderBy];
      const bv = patientOrderBy === 'state' ? canonicalState(b.state) : b[patientOrderBy];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return av - bv;
      return String(av).localeCompare(String(bv));
    };
    filtered.sort((a, b) => patientOrder === 'asc' ? cmp(a, b) : -cmp(a, b));
    return filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults, searchTerm, filterType, patientOrderBy, patientOrder]);

  const pagedPatients = visiblePatients.slice(
    patientPage * patientRowsPerPage,
    patientPage * patientRowsPerPage + patientRowsPerPage
  );

  const handlePatientSort = (field) => {
    if (patientOrderBy === field) {
      setPatientOrder(patientOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setPatientOrderBy(field);
      setPatientOrder('asc');
    }
  };

  // Reset to first page if filters shrink the list past the current page
  useEffect(() => {
    if (patientPage > 0 && pagedPatients.length === 0 && visiblePatients.length > 0) {
      setPatientPage(0);
    }
  }, [pagedPatients.length, visiblePatients.length, patientPage]);

  // Results Entry Functions
  const handleEnterResults = async (scan) => {
    setSelectedTestForResults(scan);
    setResultsDialogOpen(true);
    setScanCategory(scan.category);

    // Load scan parameters based on scan name
    await loadScanParameters(scan.scan_name);
    
    // Load existing results for today
    await loadTodayExistingResults(scan);
  };

  const loadTodayExistingResults = async (scan) => {
    try {
      const currentDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      
      const response = await fetch(loadExistingScanResults, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patientId: scan.patient_id,
          scanName: scan.scan_name,
          date: currentDate
        })
      });

      const data = await response.json();

      if (data.success && data.results && data.results.length > 0) {
        // Transform existing results to match our format
        const transformedResults = data.results.map(result => ({
          parameterId: result.id,
          parameterName: result.para,
          result: result.finding,
          flag: result.flag || '-'
        }));
        setEnteredResults(transformedResults);
      } else {
        setEnteredResults([]);
      }
    } catch (error) {
      setEnteredResults([]);
    }
  };

  const loadScanParameters = async (scanName) => {
    setLoadingParameters(true);
    try {
      const response = await fetch(loadScanParametersAPI(scanName));
      const data = await response.json();

      if (data.success && data.parameters) {
        // Transform API response to match expected format
        const transformedParameters = data.parameters.map((param, index) => ({
          id: param.id,
          name: param.paramater,
          flags: param.Flag && param.Flag.trim() !== '' ? param.Flag.split(',').map(flag => flag.trim()) : []
        }));
        setScanParameters(transformedParameters);
      } else {
        setScanParameters([]);
      }
    } catch (error) {
      console.error('Error loading scan parameters:', error);
      setScanParameters([]);
    } finally {
      setLoadingParameters(false);
    }
  };

  const handleParameterSelect = async (parameter) => {
    setSelectedParameter(parameter);
    setParameterResult('');
    // Set default flag based on available flags
    const defaultFlag = parameter.flags && parameter.flags.length > 0 ? parameter.flags[0].toLowerCase() : '-';
    setParameterFlag(defaultFlag);
    // Set available flags for this parameter
    setAvailableFlags(parameter.flags && parameter.flags.length > 0 ? parameter.flags : ['-']);
  };

  const handleAddResult = () => {
    if (!selectedParameter || !parameterResult.trim()) return;

    const newResult = {
      parameterId: selectedParameter.id,
      parameterName: selectedParameter.name,
      result: parameterResult,
      flag: parameterFlag
    };

    if (editingIndex !== null) {
      // Update existing result
      const updatedResults = [...enteredResults];
      updatedResults[editingIndex] = newResult;
      setEnteredResults(updatedResults);
      setEditingIndex(null);
    } else {
      // Add new result
      setEnteredResults([...enteredResults, newResult]);
    }

    // Reset form
    setSelectedParameter(null);
    setParameterResult('');
    setParameterFlag('normal');
  };

  const handleSaveResults = async () => {
    if (enteredResults.length === 0) return;

    // Show confirmation dialog
    const result = await Swal.fire({
      title: 'Save Results?',
      text: 'Do you want to save these scan results?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Save',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) {
      return;
    }

    // Show progress
    Swal.fire({
      title: 'Saving...',
      text: 'Please wait while we save your results',
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      // Get current user and date
      const currentUser = userInfo.user_name || userInfo.name || 'Unknown';
      const currentDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

      // Prepare data for API
      const saveData = {
        patientId: selectedTestForResults.patient_id,
        scanName: selectedTestForResults.scan_name,
        results: enteredResults.map(result => ({
          parameterName: result.parameterName,
          result: result.result,
          flag: result.flag
        })),
        user: currentUser,
        date: currentDate
      };

      // Call the API to save results
      const response = await fetch(saveScanResults, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(saveData)
      });

      const data = await response.json();

      if (data.success) {
        // Close progress dialog and show success
        Swal.fire({
          title: 'Done!',
          text: 'Scan results have been saved successfully',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });

        // Keep the scan status as is (don't set to completed)
        // This allows editing again without navigating away
        const updatedScans = patientScans.map(scan =>
          scan.id === selectedTestForResults.id
            ? { ...scan, results: enteredResults }
            : scan
        );
        setPatientScans(updatedScans);

        // Reset form
        setEnteredResults([]);
        setGeneralComment('');
        setResultsDialogOpen(false);
        setSelectedTestForResults(null);
        setScanParameters([]);
      } else if (response.status === 409 && data?.paymentRequired) {
        /* Unpaid, not broken. fetch() does not throw on 4xx, so this has to be
           checked on the response rather than in the catch. Warning icon, and
           reload — the cash desk may have taken payment while this dialog was
           open. */
        await Swal.fire({
          icon: 'warning',
          title: 'Payment required',
          text: data.message || 'Payment has not been recorded for this scan yet.',
          confirmButtonColor: '#b45309',
        });
        setResultsDialogOpen(false);
        if (selectedPatient?.id) {
          loadPatientScanRequests(selectedPatient.id, selectedPatient.state);
        }
      } else {
        Swal.fire({
          title: 'Error',
          text: data?.message || 'Failed to save results. Please try again.',
          icon: 'error'
        });
      }
    } catch (error) {
      console.error('Error saving results:', error);
      Swal.fire({
        title: 'Error',
        text: 'An error occurred while saving. Please try again.',
        icon: 'error'
      });
    }
  };

  const handleViewReport = async () => {
    if (!selectedPatient) return;
    
    setReportLoading(true);
    try {
      const response = await fetch(loadPatientScanReport, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patientId: selectedPatient.id
        })
      });

      const data = await response.json();

      if (data.success) {
        setReportData(data.reportData || {});
        setReportDialogOpen(true);
      }
      setReportLoading(false);
    } catch (error) {
      setReportLoading(false);
    }
  };

  const handleCloseReport = () => {
    setReportDialogOpen(false);
    setReportData({});
  };

  const handleViewTodayResults = async () => {
    try {
      const response = await fetch(loadPatientScanResultsToday, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patientId: selectedPatient?.id
        })
      });

      const data = await response.json();

      if (data.success && data.reportData && Object.keys(data.reportData).length > 0) {
        // Generate HTML5 report and open in new window
        const htmlContent = generateScanReportHTML(data.reportData, selectedPatient, userInfo, companyInfo);
        const printWindow = window.open('', '_blank');
        printWindow.document.write(htmlContent);
        printWindow.document.close();
      } else {
        Swal.fire({
          icon: 'info',
          title: 'No Results',
          text: 'No scan results found for today.'
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load today\'s results.'
      });
    }
  };

  const handleEditResult = (index) => {
    const result = enteredResults[index];
    const parameter = scanParameters.find(p => p.id === result.parameterId);
    setSelectedParameter(parameter || {
      id: result.parameterId,
      name: result.parameterName
    });
    setParameterResult(result.result);
    setParameterFlag(result.flag);
    // Set available flags for this parameter
    setAvailableFlags(parameter && parameter.flags && parameter.flags.length > 0 ? parameter.flags : ['-']);
    setEditingIndex(index);
  };

  const handleRemoveResult = (index) => {
    const updatedResults = enteredResults.filter((_, i) => i !== index);
    setEnteredResults(updatedResults);
    if (editingIndex === index) {
      setEditingIndex(null);
      setSelectedParameter(null);
      setParameterResult('');
      setParameterFlag('normal');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'warning';
      case 'pending': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'normal': return 'info';
      default: return 'default';
    }
  };



  return (
    <Box sx={{ width: '100%', p: 3 }}>
      {/* Page header — toggles between worklist title and patient breadcrumb+back */}
      {selectedPatient ? (
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            px: { xs: 2, md: 2.5 },
            py: 1.5,
            border: '1px solid #e5e9ef',
            borderRadius: 2.5,
            bgcolor: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            boxShadow: '0 2px 8px rgba(15, 23, 42, 0.03)',
          }}
        >
          <Button
            onClick={handleBackToWorklist}
            startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: '#1A9B7F',
              borderRadius: 1.75,
              px: 1.75,
              '&:hover': { bgcolor: 'rgba(26,155,127,0.08)' },
            }}
          >
            Back to Worklist
          </Button>
          <Box sx={{ width: '1px', height: 28, bgcolor: '#e5e9ef' }} />
          <Box sx={{ minWidth: 0, flexShrink: 1 }}>
            <Typography sx={{
              fontSize: '0.7rem', fontWeight: 700,
              color: '#1A9B7F', letterSpacing: '1.4px',
              textTransform: 'uppercase', lineHeight: 1,
            }}>
              Patient Scans
            </Typography>
            <Typography sx={{
              fontSize: { xs: '1rem', md: '1.15rem' },
              fontWeight: 700,
              color: '#0f172a',
              mt: 0.25,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {selectedPatient.fullName}
              <Box component="span" sx={{ fontWeight: 500, color: '#64748b', ml: 1, fontSize: '0.88rem' }}>
                · ID {selectedPatient.id}
              </Box>
            </Typography>
          </Box>

          {/* Quick-jump search by patient ID */}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <TextField
              size="small"
              value={quickSearchId}
              onChange={(e) => setQuickSearchId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleQuickSearchById();
                }
              }}
              placeholder="Patient ID…"
              sx={{
                width: { xs: 140, md: 180 },
                '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: '#fff' },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                  </InputAdornment>
                ),
                endAdornment: quickSearchId && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setQuickSearchId('')} sx={{ color: '#94a3b8' }}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              onClick={handleQuickSearchById}
              disabled={!quickSearchId.trim()}
              variant="contained"
              size="small"
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 1.75,
                bgcolor: '#1A9B7F',
                px: 2,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#15826A', boxShadow: 'none' },
              }}
            >
              Search
            </Button>
          </Box>
        </Paper>
      ) : (
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
          Requested Scans
        </Typography>
      )}

      {/* Patient Worklist — only when no patient is selected */}
      {!selectedPatient && (
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: { xs: 2, md: 2.5 },
            border: '1px solid #e5e9ef',
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(15, 23, 42, 0.04)',
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography sx={{
              fontSize: '0.72rem', fontWeight: 700,
              color: '#1A9B7F', letterSpacing: '1.4px',
              textTransform: 'uppercase', mb: 0.5,
            }}>
              Radiology Workspace
            </Typography>
            <Typography sx={{
              fontSize: { xs: '1.25rem', md: '1.45rem' },
              fontWeight: 700, color: '#0f172a',
              letterSpacing: '-0.3px', lineHeight: 1.2,
              mb: 0.5,
            }}>
              Patient Worklist
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '0.88rem' }}>
              {(() => {
                const n = searchResults.length;
                if (filterLoading && n === 0) return 'Loading patients…';
                if (n === 0) return 'No patients currently have requested scans.';
                const word = n === 1 ? 'patient' : 'patients';
                return `${n} ${word} with requested scans. Click any row to open the chart.`;
              })()}
            </Typography>
          </Box>

          {/* Filter chips + search */}
          <Paper elevation={0} sx={{
            p: 1.5, mb: 2,
            border: '1px solid #e5e9ef', borderRadius: 2.5, bgcolor: '#fff',
          }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
                {[
                  { key: 'all',        label: 'All',        count: patientCounts.all,        color: '#475569' },
                  { key: 'InPatient',  label: 'Inpatient',  count: patientCounts.InPatient,  color: '#15803D' },
                  { key: 'OutPatient', label: 'Outpatient', count: patientCounts.OutPatient, color: '#A16207' },
                ].map(f => {
                  const active = filterType === f.key;
                  return (
                    <Chip
                      key={f.key}
                      label={`${f.label} (${f.count})`}
                      onClick={() => { setFilterType(f.key); setPatientPage(0); }}
                      size="small"
                      sx={{
                        height: 30,
                        fontWeight: 600, fontSize: '0.78rem',
                        bgcolor: active ? f.color : '#fff',
                        color:   active ? '#fff' : '#334155',
                        border: '1px solid',
                        borderColor: active ? f.color : '#e5e9ef',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: active ? f.color : `${f.color}0D`, borderColor: f.color },
                      }}
                    />
                  );
                })}
              </Stack>

              <TextField
                size="small"
                inputRef={searchInputRef}
                placeholder="Search by name, ID, phone or insurance…"
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setPatientPage(0); }}
                sx={{
                  width: { xs: '100%', md: 340 },
                  '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: '#fff' },
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm('')} sx={{ color: '#94a3b8' }}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </Paper>

          {/* Patient table */}
          <Paper elevation={0} sx={{
            border: '1px solid #e5e9ef', borderRadius: 2.5, overflow: 'hidden', bgcolor: '#fff',
          }}>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 820 }}>
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: '#f8fafc', borderBottom: '1px solid #e5e9ef' } }}>
                    {[
                      { id: 'fullName',  label: 'Patient',      sortable: true,  width: '28%' },
                      { id: 'age',       label: 'Age / Gender', sortable: true,  width: '14%' },
                      { id: 'state',     label: 'Status',       sortable: true,  width: '12%' },
                      { id: 'Insurance', label: 'Insurance',    sortable: true,  width: '18%' },
                      { id: 'visitDate', label: 'Visit Date',   sortable: true,  width: '14%' },
                      { id: 'tel',       label: 'Phone',        sortable: false, width: '12%' },
                      { id: 'actions',   label: '',             sortable: false, width: '2%', align: 'right' },
                    ].map(c => (
                      <TableCell
                        key={c.id}
                        align={c.align || 'left'}
                        sx={{
                          width: c.width,
                          fontWeight: 700, fontSize: '0.72rem',
                          color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px',
                          py: 1.25,
                        }}
                      >
                        {c.sortable ? (
                          <TableSortLabel
                            active={patientOrderBy === c.id}
                            direction={patientOrderBy === c.id ? patientOrder : 'asc'}
                            onClick={() => handlePatientSort(c.id)}
                            sx={{ '& .MuiTableSortLabel-icon': { fontSize: 16 } }}
                          >
                            {c.label}
                          </TableSortLabel>
                        ) : c.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filterLoading && searchResults.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 6, textAlign: 'center', borderBottom: 'none' }}>
                        <CircularProgress size={28} sx={{ color: '#1A9B7F' }} />
                        <Typography sx={{ color: '#64748b', fontSize: '0.9rem', mt: 1.5 }}>
                          Loading patients…
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : pagedPatients.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 6, textAlign: 'center', borderBottom: 'none' }}>
                        <PersonIcon sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
                        <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
                          {searchTerm || filterType !== 'all'
                            ? 'No patients match your filters.'
                            : 'No patients to show.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : pagedPatients.map((p) => {
                    const state = canonicalState(p.state);
                    const isInpatient = state === 'InPatient';
                    const isChild = (p.age != null) && Number(p.age) <= 12;
                    return (
                      <TableRow
                        key={p.id}
                        hover
                        onClick={() => handlePatientSelect(p)}
                        sx={{
                          cursor: 'pointer',
                          '& td': { borderBottom: '1px solid #f1f5f9', py: 1.25 },
                          '&:hover .row-arrow': { opacity: 1, transform: 'translateX(2px)' },
                          '&:last-child td': { borderBottom: 'none' },
                        }}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar sx={{
                              width: 36, height: 36, fontSize: 13, fontWeight: 700,
                              bgcolor: isChild ? '#FCE7F3' : 'rgba(26,155,127,0.12)',
                              color:   isChild ? '#BE185D' : '#1A9B7F',
                            }}>
                              {isChild ? <ChildCareIcon sx={{ fontSize: 18 }} /> : initialsOf(p.fullName)}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{
                                fontWeight: 600, fontSize: '0.88rem', color: '#0f172a',
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              }}>
                                {p.fullName || 'Unknown'}
                              </Typography>
                              <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>
                                ID: {p.id}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>
                            {p.age ?? '—'} <Box component="span" sx={{ color: '#94a3b8', fontWeight: 400 }}>yrs</Box>
                          </Typography>
                          <Chip
                            label={p.gender || '—'}
                            size="small"
                            sx={{
                              mt: 0.25, height: 18, fontSize: '0.65rem', fontWeight: 700,
                              bgcolor: String(p.gender).toUpperCase() === 'MALE' ? '#EFF6FF' : '#FDF2F8',
                              color:   String(p.gender).toUpperCase() === 'MALE' ? '#1D4ED8' : '#BE185D',
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={state}
                            size="small"
                            sx={{
                              height: 22, fontSize: '0.7rem', fontWeight: 700,
                              bgcolor: isInpatient ? '#DCFCE7' : '#FEF3C7',
                              color:   isInpatient ? '#15803D' : '#A16207',
                              '& .MuiChip-label': { px: 1 },
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontSize: '0.82rem', color: '#475569' }}>
                            {p.Insurance || '—'}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontSize: '0.82rem', color: '#334155' }}>
                            {p.visitDate
                              ? new Date(p.visitDate).toISOString().slice(0, 10)
                              : '—'}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontSize: '0.8rem', color: '#475569' }}>
                            {p.tel || '—'}
                          </Typography>
                        </TableCell>

                        <TableCell align="right">
                          <Tooltip title="Open chart" arrow>
                            <IconButton
                              size="small"
                              className="row-arrow"
                              onClick={(e) => { e.stopPropagation(); handlePatientSelect(p); }}
                              sx={{
                                color: '#1A9B7F',
                                opacity: 0.55,
                                transition: 'opacity .15s ease, transform .15s ease',
                                '&:hover': { bgcolor: 'rgba(26,155,127,0.12)', opacity: 1 },
                              }}
                            >
                              <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            {visiblePatients.length > patientRowsPerPage && (
              <TablePagination
                component="div"
                count={visiblePatients.length}
                page={patientPage}
                onPageChange={(_, p) => setPatientPage(p)}
                rowsPerPage={patientRowsPerPage}
                onRowsPerPageChange={(e) => { setPatientRowsPerPage(parseInt(e.target.value, 10)); setPatientPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{ borderTop: '1px solid #e5e9ef' }}
              />
            )}
          </Paper>
        </Paper>
      )}

      {/* Patient detail panel — bio/vitals + scans, only when a patient is selected */}
      {selectedPatient && (
        <>
          <Card
            sx={{
              mb: 3,
              borderRadius: 4,
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '100%',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.98) 100%)',
                zIndex: 1
              }
            }}
          >
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 2 }}>
              <Grid container spacing={2} wrap="nowrap">
                {/* Patient Profile Section */}
                <Grid item xs={2.4}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      p: 3,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #0fd34aff 0%, #319249ff 100%)',
                      color: 'white',
                      boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: '-50%',
                        right: '-50%',
                        width: '200%',
                        height: '200%',
                        background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                        pointerEvents: 'none'
                      }
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 100,
                        height: 100,
                        bgcolor: 'rgba(255, 255, 255, 0.25)',
                        border: '4px solid rgba(255, 255, 255, 0.3)',
                        mb: 2,
                        fontSize: '2.5rem',
                        fontWeight: 700,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        backdropFilter: 'blur(10px)',
                        zIndex: 1
                      }}
                    >
                      {selectedPatient.fullName?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'P'}
                    </Avatar>
                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 1, textShadow: '0 2px 4px rgba(0,0,0,0.1)', zIndex: 1 }}>
                      {selectedPatient.fullName}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2, opacity: 0.95, zIndex: 1 }}>
                      {selectedPatient.age} years old
                    </Typography>
                    <Box
                      sx={{
                        px: 2.5,
                        py: 0.75,
                        borderRadius: 2,
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        mb: 2,
                        zIndex: 1
                      }}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        ID: {selectedPatient.id}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ mb: 2, zIndex: 1, flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
                      <Chip
                        label={selectedPatient.gender || '—'}
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.25)',
                          color: 'white',
                          fontWeight: 600,
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          backdropFilter: 'blur(10px)'
                        }}
                      />
                      <Chip
                        label={selectedPatient.Insurance || 'N/A'}
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.25)',
                          color: 'white',
                          fontWeight: 600,
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          backdropFilter: 'blur(10px)'
                        }}
                      />
                    </Stack>
                    <Chip
                      label={selectedPatient.state || '—'}
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.25)',
                        color: 'white',
                        fontWeight: 600,
                        px: 2,
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        backdropFilter: 'blur(10px)',
                        zIndex: 1
                      }}
                    />
                  </Box>
                </Grid>

                {/* Contact Information Section */}
                <Grid item xs={2.4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 3,
                      bgcolor: '#f8f9fd',
                      border: '1px solid #e8eaf6',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                        transform: 'translateY(-4px)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          mr: 2
                        }}
                      >
                        <PhoneIcon sx={{ color: 'white', fontSize: 24 }} />
                      </Box>
                      <Typography variant="h6" fontWeight={700} color="primary">
                        Contact Info
                      </Typography>
                    </Box>

                    <Stack spacing={2.5}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Phone Number
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                          {selectedPatient.tel || 'Not provided'}
                        </Typography>
                      </Box>

                      <Divider />

                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Location
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                          {selectedPatient.res || 'Not provided'}
                        </Typography>
                      </Box>

                      <Divider />

                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Visit Date
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5, color: 'primary.main' }}>
                          {selectedPatient.visitDate || '—'}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                {/* Vital Signs Section */}
                <Grid item xs={2.4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 3,
                      bgcolor: '#fff3e0',
                      border: '1px solid #ffe0b2',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 8px 24px rgba(255, 152, 0, 0.15)',
                        transform: 'translateY(-4px)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                          mr: 2
                        }}
                      >
                        <HospitalIcon sx={{ color: 'white', fontSize: 24 }} />
                      </Box>
                      <Typography variant="h6" fontWeight={700} sx={{ color: '#e65100' }}>
                        Vital Signs
                      </Typography>
                    </Box>

                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          Temperature
                        </Typography>
                        <Chip
                          label={selectedPatient.vitalSigns?.temperature || '-'}
                          size="small"
                          sx={{ fontWeight: 700, bgcolor: 'rgba(255, 152, 0, 0.15)', color: '#e65100' }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          Blood Pressure
                        </Typography>
                        <Chip
                          label={selectedPatient.vitalSigns?.bp || '-'}
                          size="small"
                          sx={{ fontWeight: 700, bgcolor: 'rgba(255, 152, 0, 0.15)', color: '#e65100' }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          Pulse
                        </Typography>
                        <Chip
                          label={selectedPatient.vitalSigns?.pulse || '-'}
                          size="small"
                          sx={{ fontWeight: 700, bgcolor: 'rgba(255, 152, 0, 0.15)', color: '#e65100' }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          Respiratory Rate
                        </Typography>
                        <Chip
                          label={selectedPatient.vitalSigns?.respiration || '-'}
                          size="small"
                          sx={{ fontWeight: 700, bgcolor: 'rgba(255, 152, 0, 0.15)', color: '#e65100' }}
                        />
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                {/* Measurements Section */}
                <Grid item xs={2.4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 3,
                      bgcolor: '#e8f5e9',
                      border: '1px solid #c8e6c9',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 8px 24px rgba(76, 175, 80, 0.15)',
                        transform: 'translateY(-4px)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                          mr: 2
                        }}
                      >
                        <ScienceIcon sx={{ color: 'white', fontSize: 24 }} />
                      </Box>
                      <Typography variant="h6" fontWeight={700} sx={{ color: '#2e7d32' }}>
                        Measurements
                      </Typography>
                    </Box>

                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          Weight
                        </Typography>
                        <Chip
                          label={selectedPatient.vitalSigns?.weight || '-'}
                          size="small"
                          sx={{ fontWeight: 700, bgcolor: 'rgba(76, 175, 80, 0.15)', color: '#2e7d32' }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          Height
                        </Typography>
                        <Chip
                          label={selectedPatient.vitalSigns?.height || '-'}
                          size="small"
                          sx={{ fontWeight: 700, bgcolor: 'rgba(76, 175, 80, 0.15)', color: '#2e7d32' }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          BMI
                        </Typography>
                        <Chip
                          label={selectedPatient.vitalSigns?.bmi || '-'}
                          size="small"
                          sx={{ fontWeight: 700, bgcolor: 'rgba(76, 175, 80, 0.15)', color: '#2e7d32' }}
                        />
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                {/* Actions Section */}
                <Grid item xs={2.4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 3,
                      bgcolor: '#f3e8ff',
                      border: '1px solid #ddd6fe',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 8px 24px rgba(124, 58, 237, 0.18)',
                        transform: 'translateY(-4px)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                          mr: 2
                        }}
                      >
                        <ActionsIcon sx={{ color: 'white', fontSize: 24 }} />
                      </Box>
                      <Typography variant="h6" fontWeight={700} sx={{ color: '#5b21b6' }}>
                        Actions
                      </Typography>
                    </Box>

                    <Stack spacing={1.25}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="small"
                        onClick={handleViewTodayResults}
                        disabled={patientScans.length === 0}
                        sx={{
                          textTransform: 'none', fontWeight: 600, borderRadius: 1.75,
                          py: 0.9,
                          backgroundColor: '#43a047',
                          boxShadow: 'none',
                          '&:hover': { backgroundColor: '#388e3c', boxShadow: '0 4px 12px rgba(67,160,71,0.25)' }
                        }}
                      >
                        Today's Results
                      </Button>
                      <Button
                        fullWidth
                        variant="contained"
                        size="small"
                        onClick={handleViewReport}
                        disabled={reportLoading || patientScans.length === 0}
                        sx={{
                          textTransform: 'none', fontWeight: 600, borderRadius: 1.75,
                          py: 0.9,
                          backgroundColor: '#1976d2',
                          boxShadow: 'none',
                          '&:hover': { backgroundColor: '#1565c0', boxShadow: '0 4px 12px rgba(25,118,210,0.25)' }
                        }}
                      >
                        {reportLoading ? <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} /> : null}
                        View Report
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        onClick={handleViewDocumentation}
                        disabled={documentationLoading}
                        sx={{
                          textTransform: 'none', fontWeight: 600, borderRadius: 1.75,
                          py: 0.9,
                          borderColor: '#ff9800', color: '#ff9800',
                          '&:hover': {
                            borderColor: '#f57c00',
                            backgroundColor: 'rgba(255, 152, 0, 0.06)'
                          }
                        }}
                      >
                        {documentationLoading ? <CircularProgress size={16} sx={{ mr: 1, color: '#ff9800' }} /> : null}
                        View Documentation
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        onClick={() => setMedicalHistoryOpen(true)}
                        sx={{
                          textTransform: 'none', fontWeight: 600, borderRadius: 1.75,
                          py: 0.9,
                          borderColor: '#7c3aed', color: '#7c3aed',
                          '&:hover': {
                            borderColor: '#5b21b6',
                            backgroundColor: 'rgba(124, 58, 237, 0.06)'
                          }
                        }}
                      >
                        Medical History
                      </Button>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Patient Scans */}
          <Card>
          <CardHeader
            title={`Scans for ${selectedPatient.fullName}`}
            avatar={<ScannerIcon />}
            action={
              <Chip
                label={`${patientScans.length} scan${patientScans.length === 1 ? '' : 's'}`}
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            }
          />
          <CardContent sx={{ p: 0 }}>
            {patientScans.length === 0 ? (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <ScannerIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No scans requested for this patient
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Scan Name</TableCell>
                      <TableCell>Requested By</TableCell>
                      <TableCell>Requested Date</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {patientScans
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((scan) => (
                      <TableRow key={scan.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                              <ScannerIcon sx={{ fontSize: 16 }} />
                            </Avatar>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {scan.scan_name}
                                </Typography>
                                {/* Two wordings on purpose: "may run" and
                                    "payment required" are different
                                    instructions to the radiographer. */}
                                {isUnpaidScan(scan) && (
                                  <Chip
                                    label={unpaidPolicy.canServeUnpaid
                                      ? 'UNPAID · may run'
                                      : 'UNPAID · payment required'}
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: '0.65rem',
                                      fontWeight: 700,
                                      bgcolor: unpaidPolicy.canServeUnpaid
                                        ? 'rgba(217,119,6,0.14)'
                                        : 'rgba(220,38,38,0.12)',
                                      color: unpaidPolicy.canServeUnpaid ? '#A16207' : '#991B1B',
                                    }}
                                  />
                                )}
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                {scan.scan_code}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell>{scan.doc_name}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {scan.requested_date}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {scan.requested_time}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {scan.status !== 'completed' && (
                              <Tooltip title={isBlockedScan(scan)
                                ? 'Payment has not been recorded for this scan. Results cannot be entered yet.'
                                : 'Enter Results'}>
                                <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  disabled={isBlockedScan(scan)}
                                  onClick={() => handleEnterResults(scan)}
                                >
                                  <EditIcon />
                                </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={patientScans.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableContainer>
            )}
          </CardContent>
        </Card>
        </>
      )}

      {/* Results Entry Dialog */}
      <Dialog
        open={resultsDialogOpen}
        onClose={() => setResultsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Enter Results - {selectedTestForResults?.scan_name}
        </DialogTitle>
        <DialogContent>
          <div className="row">
            <div className="col-md-6 col-12">
              <Typography variant="h6" sx={{ mb: 1 }}>Select Parameter</Typography>
              <List sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                {scanParameters.map((param) => (
                  <ListItemButton
                    key={param.id}
                    selected={selectedParameter?.id === param.id}
                    onClick={() => handleParameterSelect(param)}
                  >
                    <ListItemText
                      primary={param.name}
                    />
                  </ListItemButton>
                ))}
              </List>
            </div>

            <div className="col-md-6 col-12">
              <Typography variant="h6" sx={{ mb: 1 }}>Enter Result</Typography>
              {selectedParameter && (
                <div className="row">
                  <div className="col-12">
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Result"
                      value={parameterResult}
                      onChange={(e) => setParameterResult(e.target.value)}
                      placeholder={`Enter result for ${selectedParameter.name}`}
                    />
                  </div>
                  <div className="col-12">
                    <FormControl fullWidth sx={{ mt: 1 }}>
                      <InputLabel>Flag</InputLabel>
                      <Select
                        value={parameterFlag}
                        onChange={(e) => setParameterFlag(e.target.value)}
                        label="Flag"
                      >
                        {availableFlags.map((flag) => (
                          <MenuItem key={flag} value={flag.toLowerCase()}>
                            {flag}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                  <div className="col-12">
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleAddResult}
                      disabled={!parameterResult.trim()}
                    >
                      {editingIndex !== null ? 'Update Result' : 'Add Result'}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div className="col-12">
              <Divider sx={{ my: 1 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>Entered Results</Typography>
              <List>
                {enteredResults.map((result, index) => (
                  <ListItem key={index} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 0.5, py: 0.5 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {result.parameterName}
                          </Typography>
                          <Chip
                            label={result.flag}
                            size="small"
                            color={result.flag === 'normal' ? 'success' : 'warning'}
                          />
                        </Box>
                      }
                      secondary={`Result: ${result.result}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton size="small" onClick={() => handleEditResult(index)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleRemoveResult(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </div>

            <div className="col-12">
              <TextField
                fullWidth
                multiline
                rows={2}
                label="General Comment"
                value={generalComment}
                onChange={(e) => setGeneralComment(e.target.value)}
                placeholder="Add any general comments or impressions..."
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResultsDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveResults}
            variant="contained"
            disabled={enteredResults.length === 0}
            startIcon={<SaveIcon />}
          >
            Save Results
          </Button>
        </DialogActions>
      </Dialog>

      {/* Report Dialog */}
      <Dialog 
        open={reportDialogOpen} 
        onClose={handleCloseReport}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Scan Report - {selectedPatient?.fullName}
            </Typography>
            {reportLoading && <CircularProgress size={24} />}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {Object.keys(reportData).length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                No scan results found for this patient
              </Typography>
            </Box>
          ) : (
            <Box>
              {Object.keys(reportData)
                .sort((a, b) => new Date(b) - new Date(a)) // Sort dates descending (most recent first)
                .map((date) => (
                  <Box key={date} sx={{ mb: 4 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600, 
                        color: '#1976d2',
                        borderBottom: '2px solid #1976d2',
                        pb: 1,
                        mb: 2
                      }}
                    >
                      {(() => {
                        const [year, month, day] = date.split('-').map(Number);
                        const localDate = new Date(year, month - 1, day);
                        return localDate.toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        });
                      })()}
                    </Typography>

                    {Object.keys(reportData[date])
                      .sort() // Sort scan names alphabetically
                      .map((scanName) => (
                        <Box key={scanName} sx={{ ml: 2, mb: 3 }}>
                          <Typography 
                            variant="subtitle1" 
                            sx={{ 
                              fontWeight: 600,
                              color: '#424242',
                              mb: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            🔬 {scanName}
                          </Typography>

                          <TableContainer sx={{ ml: 2 }}>
                            <Table size="small" sx={{ border: '1px solid #e0e0e0' }}>
                              <TableHead>
                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                  <TableCell sx={{ fontWeight: 600 }}>Parameter</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Finding/Result</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Flag</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Performed By</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {reportData[date][scanName].map((result, idx) => (
                                  <TableRow key={idx} hover>
                                    <TableCell>{result.para}</TableCell>
                                    <TableCell>{result.finding}</TableCell>
                                    <TableCell>
                                      <Chip
                                        label={result.flag}
                                        size="small"
                                        color={result.flag === 'normal' || result.flag === '-' ? 'success' : 'warning'}
                                        variant="outlined"
                                      />
                                    </TableCell>
                                    <TableCell>{result.user}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      ))}
                  </Box>
                ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReport} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Documentation Dialog */}
      <Dialog
        open={documentationDialogOpen}
        onClose={handleCloseDocumentation}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              px: 3, py: 2.25,
              color: 'white',
              display: 'flex', alignItems: 'center', gap: 2,
            }}
          >
            <Box sx={{
              width: 44, height: 44, borderRadius: 2,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.18)',
              border: '1px solid rgba(255,255,255,0.28)',
              backdropFilter: 'blur(10px)',
              fontWeight: 800, fontSize: '1.1rem',
            }}>
              {(selectedPatient?.fullName || '?')
                .split(/\s+/).filter(Boolean).slice(0, 2)
                .map(n => n[0]).join('').toUpperCase()}
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography sx={{
                fontSize: '0.68rem', fontWeight: 700, letterSpacing: '1.4px',
                textTransform: 'uppercase', opacity: 0.85, lineHeight: 1,
              }}>
                Clinical Notes
              </Typography>
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, mt: 0.4, lineHeight: 1.2 }}>
                Patient Documentation
              </Typography>
              {selectedPatient && (
                <Typography sx={{ fontSize: '0.82rem', opacity: 0.9, mt: 0.25 }}>
                  {selectedPatient.fullName} · ID {selectedPatient.id}
                </Typography>
              )}
            </Box>
            {documentationLoading && <CircularProgress size={24} sx={{ color: 'white' }} />}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ bgcolor: '#f8fafc', p: { xs: 2, md: 3 } }}>
          {documentationData ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {[
                { key: 'complaint', label: 'Chief Complaint',      text: documentationData.complaint, color: '#dc2626', tint: '#fef2f2', badge: '1' },
                { key: 'history',   label: 'History of Complaint', text: documentationData.history,   color: '#2563eb', tint: '#eff6ff', badge: '2' },
                { key: 'physical',  label: 'Physical Examination', text: documentationData.physical,  color: '#15803d', tint: '#f0fdf4', badge: '3' },
              ].map((s) => (
                <Paper
                  key={s.key}
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid #e5e9ef',
                    overflow: 'hidden',
                    bgcolor: '#fff',
                    transition: 'box-shadow 0.2s ease',
                    '&:hover': { boxShadow: '0 4px 16px rgba(15,23,42,0.06)' },
                  }}
                >
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    px: 2, py: 1.25,
                    borderLeft: '4px solid', borderLeftColor: s.color,
                    bgcolor: s.tint,
                  }}>
                    <Box sx={{
                      width: 28, height: 28, borderRadius: 1.5,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: s.color, color: 'white',
                      fontWeight: 800, fontSize: '0.85rem',
                      boxShadow: `0 2px 6px ${s.color}40`,
                    }}>
                      {s.badge}
                    </Box>
                    <Typography sx={{
                      fontWeight: 700, color: s.color,
                      fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.6px',
                    }}>
                      {s.label}
                    </Typography>
                  </Box>
                  <Box sx={{ px: 3, py: 2.25 }}>
                    {s.text && String(s.text).trim() ? (
                      <Typography sx={{
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.75,
                        color: '#334155',
                        fontSize: '0.93rem',
                      }}>
                        {s.text}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontStyle: 'italic', color: '#94a3b8', fontSize: '0.88rem' }}>
                        No {s.label.toLowerCase()} recorded for this patient.
                      </Typography>
                    )}
                  </Box>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Box sx={{
                width: 64, height: 64, borderRadius: '50%',
                bgcolor: '#e2e8f0', mx: 'auto', mb: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#64748b', fontWeight: 700, fontSize: '1.5rem',
              }}>
                ?
              </Box>
              <Typography sx={{ color: '#334155', fontWeight: 600 }}>
                No documentation available
              </Typography>
              <Typography sx={{ color: '#64748b', fontSize: '0.85rem', mt: 0.5 }}>
                Nothing has been recorded for this patient yet.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ bgcolor: '#f8fafc', px: 3, py: 2, borderTop: '1px solid #e5e9ef' }}>
          <Button
            onClick={handleCloseDocumentation}
            variant="contained"
            sx={{
              textTransform: 'none', fontWeight: 700, borderRadius: 1.75,
              px: 3, boxShadow: 'none',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': { boxShadow: '0 4px 12px rgba(102,126,234,0.4)' },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <MedicalHistoryDialog
        open={medicalHistoryOpen}
        onClose={() => setMedicalHistoryOpen(false)}
        patientData={selectedPatient}
      />
    </Box>
  );
};

export default RequestedScan;